// ─────────────────────────────────────────────────────────────────────
// /admin/access — Access Management. Built in Batch A (Rounds 3 & 4, 2026).
//
// A single IT-Admin page that consolidates role and permission
// administration into tabs:
//
//   • Roles                  (Round 3) — create / rename / enable-disable /
//                                          safe-delete roles
//   • Role Permissions       (Round 4) — assign/remove permissions (modules)
//                                          for a selected role
//   • Employee Role Assignment (Round 5) — assign/clear a role per employee
//   • Direct User Permissions  (Round 6) — grant/revoke a direct permission
//   • Effective Access Preview (Round 7) — read-only "what can they access & why"
//
// The existing standalone pages (/admin/employee-roles,
// /admin/user-permissions) are intentionally LEFT IN PLACE and untouched;
// these tabs reuse the SAME endpoints. The standalone pages can be retired
// later once the consolidated page is fully validated. Nothing is removed.
//
// Uses the project's own ResponsiveTabs (DashboardKit) for the tab strip —
// same visual language as the rest of the dashboard, no new dependency.
//
// Route guarded by <RoleProtected requireModule="admin.access"> in
// allRoutes.js. Every backend endpoint these tabs call is policy-gated
// (CanManageAccess / CanManageEmployeeRoles / CanManageUserPermissions), so
// frontend hiding is defense-in-depth, not the security boundary.
//
// Access-Management hardening (Round 1, decision D3 — fine-grained
// delegation): each tab is shown only to callers holding the permission that
// backs its API, so an "IT Staff" role with, say, only "Admin - Employee
// Roles" sees only the Employee Role Assignment tab:
//   Roles / Role Permissions / Effective Access Preview → admin.access
//   Employee Role Assignment                            → admin.employeeRoles
//   Direct User Permissions                             → admin.userPermissions
// IT Admin sees everything (canAccessModule is true for every key).
// ─────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import useAuth from "../../Personal Attendance/useAuth";
import { Container, Card, CardBody, Alert } from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DashboardPageHeader, ResponsiveTabs } from "../../../Components/Common/DashboardKit";
import RolesTab from "./RolesTab";
import RolePermissionsTab from "./RolePermissionsTab";
import EmployeeAssignmentTab from "./EmployeeAssignmentTab";
import DirectPermissionsTab from "./DirectPermissionsTab";
import EffectiveAccessPreviewTab from "./EffectiveAccessPreviewTab";
import AccountSecurityTab from "./AccountSecurityTab";   // Round 12

const TABS = [
    { key: "roles",      label: "Roles",                    icon: "ri-shield-user-line",   module: "admin.access" },
    { key: "rolePerms",  label: "Role Permissions",         icon: "ri-key-2-line",         module: "admin.access" },
    { key: "assignment", label: "Employee Role Assignment", icon: "ri-user-settings-line", module: "admin.employeeRoles" },
    { key: "direct",     label: "Direct User Permissions",  icon: "ri-user-add-line",      module: "admin.userPermissions" },
    { key: "preview",    label: "Effective Access Preview", icon: "ri-eye-line",           module: "admin.access" },
    { key: "security",   label: "Account Security",         icon: "ri-lock-password-line", module: "admin.accountSecurity" },   // Round 12
];

export default function AccessManagement() {
    document.title = "Access Management | SSW Technologies";

    const { isItAdmin, canAccessModule } = useAuth();
    const visibleTabs = useMemo(
        () => TABS.filter((t) => isItAdmin || canAccessModule(t.module)),
        
        [isItAdmin]
    );
    const [activeTab, setActiveTab] = useState(() => {
        // Round 12: /admin/access?tab=security opens that tab when visible.
        try {
            const wanted = new URLSearchParams(window.location.search).get("tab");
            if (wanted && visibleTabs.some((t) => t.key === wanted)) return wanted;
        } catch { /* ignore */ }
        return visibleTabs[0]?.key ?? "roles";
    });
    const canSee = (key) => visibleTabs.some((t) => t.key === key);

    // Lifting one piece of state: when a role is selected/created on the Roles
    // tab, the Role Permissions tab should default to it. Kept here so the two
    // tabs stay in sync without prop-drilling through a store.
    const [focusedRoleId, setFocusedRoleId] = useState(null);

    const goToRolePerms = (roleId) => {
        setFocusedRoleId(roleId ?? null);
        setActiveTab("rolePerms");
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-shield-keyhole-line"
                    eyebrow="Administration"
                    title="Access Management"
                    subtitle="Create roles, map modules and permissions to them, assign roles to employees, and preview effective access."
                    tone="rose"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Admin", to: "/admin" },
                        { label: "Access Management" },
                    ]}
                />

                <Alert color="info" className="d-flex align-items-center mb-3">
                    <i className="ri-information-line me-2 fs-18" />
                    <span>
                        Role and permission changes apply immediately to module access and to the
                        person's next session refresh (within 30 minutes) for everything else — no
                        re-login needed. Deactivating an employee or changing their role or password
                        signs them out everywhere at once.
                    </span>
                </Alert>

                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <ResponsiveTabs
                            tabs={visibleTabs}
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            color="primary"
                        />

                        {visibleTabs.length === 0 && (
                            <div className="text-muted py-4 text-center">
                                You do not hold any Access Management permission.
                            </div>
                        )}

                        {/* Render only the active tab (preserves the dashboard's
                            "render only active tab" convention). Each tab is
                            additionally gated by its backing permission. */}
                        {activeTab === "roles" && canSee("roles") && (
                            <RolesTab onManagePermissions={goToRolePerms} />
                        )}

                        {activeTab === "rolePerms" && canSee("rolePerms") && (
                            <RolePermissionsTab initialRoleId={focusedRoleId} />
                        )}

                        {activeTab === "assignment" && canSee("assignment") && <EmployeeAssignmentTab />}

                        {activeTab === "direct" && canSee("direct") && <DirectPermissionsTab />}

                        {activeTab === "preview" && canSee("preview") && <EffectiveAccessPreviewTab />}

                        {activeTab === "security" && canSee("security") && <AccountSecurityTab />}
                    </CardBody>
                </Card>
            </Container>

            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
}
